import type {SchematicPage, SchematicPageGroup} from '../../../../generated/crysknife/types.ts';

function pushToBucket<K, V>(map: Map<K, V[]>, key: K, value: V): void {
  const bucket = map.get(key);
  if (bucket) {
    bucket.push(value);
  } else {
    map.set(key, [value]);
  }
}

export type SchematicHierarchyGroup = {
  groupId: string;
  displayName: string;
  pages: SchematicHierarchyPage[];
  unanchoredChildGroups: SchematicHierarchyGroup[];
};

export type SchematicHierarchyPage = {
  pageId: string;
  name: string;
  childGroups: SchematicHierarchyGroup[];
};

export type SchematicHierarchyInputPage = Pick<SchematicPage, 'id' | 'name' | 'ui_group_id'>;

/**
 * Depth-first search over a hierarchy group: true if any page in the group or its
 * descendants satisfies `predicate`.
 */
export function groupContainsPage(
  group: SchematicHierarchyGroup,
  predicate: (pageId: string) => boolean,
): boolean {
  for (const page of group.pages) {
    if (predicate(page.pageId)) return true;
    if (page.childGroups.some((g) => groupContainsPage(g, predicate))) return true;
  }
  return group.unanchoredChildGroups.some((g) => groupContainsPage(g, predicate));
}

/**
 * Build a page-oriented hierarchy tree from crysknife's `page_groups` map and the
 * schematic's pages. Each returned root represents a group with `parent: null`.
 * Child groups nest under the specific page their block symbol lives on, via
 * `parent.reference_in_parent.parent_page_id`. Child groups without a resolvable
 * parent page hang off the parent group as `unanchoredChildGroups`.
 *
 * When base-commit data is provided, deleted pages (present in base but absent
 * from head) are placed into their base group, and base-only groups are included
 * so the hierarchy covers the full diff universe. Head data takes precedence.
 *
 * Returns `[]` when no usable hierarchy is present so the caller can fall back to
 * a flat page list.
 */
export function buildSchematicHierarchy(
  pages: readonly SchematicHierarchyInputPage[],
  pageGroups: Readonly<Record<string, SchematicPageGroup>> | null | undefined,
  basePages?: readonly SchematicHierarchyInputPage[] | null,
  basePageGroups?: Readonly<Record<string, SchematicPageGroup>> | null,
): SchematicHierarchyGroup[] {
  // Merge page_groups: head takes precedence, base fills in removed groups.
  const mergedPageGroups: Record<string, SchematicPageGroup> = {...basePageGroups, ...pageGroups};
  if (Object.keys(mergedPageGroups).length === 0) {
    return [];
  }

  const headPageIds = new Set(pages.filter((p) => p.id).map((p) => p.id));

  const pagesByGroupId = new Map<string, SchematicHierarchyInputPage[]>();
  for (const page of pages) {
    if (!page.id || !page.ui_group_id) continue;
    pushToBucket(pagesByGroupId, page.ui_group_id, page);
  }
  // Add base-only pages (deleted sheets) into their base group.
  if (basePages) {
    for (const page of basePages) {
      if (!page.id || !page.ui_group_id) continue;
      if (headPageIds.has(page.id)) continue;
      pushToBucket(pagesByGroupId, page.ui_group_id, page);
    }
  }

  const childGroupsByParentGroupId = new Map<string, SchematicPageGroup[]>();
  const rootGroups: SchematicPageGroup[] = [];
  for (const group of Object.values(mergedPageGroups)) {
    if (group.parent) {
      pushToBucket(childGroupsByParentGroupId, group.parent.id, group);
    } else {
      rootGroups.push(group);
    }
  }

  const buildNode = (group: SchematicPageGroup, ancestors: ReadonlySet<string>): SchematicHierarchyGroup => {
    const pageBucket = pagesByGroupId.get(group.id) ?? [];
    const pageIdsInGroup = new Set(pageBucket.map((p) => p.id));

    // Cycle defense: skip any child whose id is already on the ancestor chain.
    const directChildren = (childGroupsByParentGroupId.get(group.id) ?? [])
      .filter((c) => !ancestors.has(c.id));

    const childrenByPageId = new Map<string, SchematicPageGroup[]>();
    const unanchored: SchematicPageGroup[] = [];
    for (const child of directChildren) {
      const parentPageId = child.parent?.reference_in_parent?.parent_page_id ?? null;
      if (parentPageId && pageIdsInGroup.has(parentPageId)) {
        pushToBucket(childrenByPageId, parentPageId, child);
      } else {
        unanchored.push(child);
      }
    }

    const nextAncestors = new Set(ancestors);
    nextAncestors.add(group.id);

    const pages: SchematicHierarchyPage[] = pageBucket.map((page) => ({
      pageId: page.id,
      name: page.name,
      childGroups: (childrenByPageId.get(page.id) ?? []).map((c) => buildNode(c, nextAncestors)),
    }));

    return {
      groupId: group.id,
      displayName: group.display_name,
      pages,
      unanchoredChildGroups: unanchored.map((c) => buildNode(c, nextAncestors)),
    };
  };

  return rootGroups.map((g) => buildNode(g, new Set()));
}
